import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientService } from './../../../../core/services/patient.service';
import { Router } from '@angular/router';
import { RENDER_BATCH } from './../../../../shared/directives/render-more.directive';

/**
 * Read-only list of deceased patients, grouped by the care setting they were in.
 * There is deliberately no "undo" action here: a patient is deceased because they have an active
 * mortality note, so correcting a mistake means editing or deleting that note.
 */
@Component({
  selector: 'app-deceased-list',
  templateUrl: './deceased-list.component.html',
  styleUrls: ['./deceased-list.component.scss']
})
export class DeceasedListComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  /** Rows rendered so far; the rest render as the end of the list nears the screen. */
  shown = RENDER_BATCH;
  readonly renderBatch = RENDER_BATCH;
  @Input() filterValue: string = '';
  @Input() sortBy: 'surname' | 'firstName' | 'patientNumber' = 'surname';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';
  deceased: any[] = [];

  // The retained care setting each patient had before they were marked deceased.
  statusOptions: any[] = [
    { id: 'opd', name: 'Outpatients' },
    { id: 'inpatient', name: 'Inpatients' },
    { id: 'invite', name: 'Invites' }
  ];

  // This will store the sections to display – one per status option that has matching patients.
  patientStatusOptions: any[] = [];

  filteredDeceased: any[] = [];
  // This will be an object where keys are status ids and values are sorted arrays of patients.
  filteredDeceasedByStatus: { [key: string]: any[] } = {};

  public expandedSection: string | null = null;

  constructor(
    private patientService: PatientService,
    private router: Router,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.patientService.deceased$
      .pipe(takeUntil(this.destroy$))
      .subscribe(deceased => {
        this.deceased = deceased;
        this.applyFilter(); // This sets filteredDeceased and updates the grouping
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterValue'] || changes['sortBy'] || changes['sortDirection']) {
      this.applyFilter();
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  applyFilter() {
    this.shown = RENDER_BATCH;
    if (!this.deceased) {
      return;
    }

    // If no filter, show all deceased patients
    if (this.filterValue.trim() === '') {
      this.filteredDeceased = this.deceased;
    } else {
      const filterTerms = this.filterValue.toLowerCase().split(' ');
      this.filteredDeceased = _.filter(this.deceased, patient => {
        const icdCodesMatch = patient.icdCodes?.some((icdCode: any) =>
          filterTerms.every(term =>
            _.includes((icdCode.code || '').toLowerCase(), term) ||
            _.includes((icdCode.description || '').toLowerCase(), term)
          )
        );

        const patientMatch = filterTerms.every(term =>
          _.includes(patient.name.toLowerCase(), term) ||
          _.includes(patient.surname.toLowerCase(), term) ||
          _.includes(patient.patientNumber?.toLowerCase(), term) ||
          _.includes(patient.diagnosis?.toLowerCase(), term)
        );

        return icdCodesMatch || patientMatch;
      });
    }

    // After filtering, group the patients by their retained status.
    this.groupFilteredDeceasedByStatus();
  }

  groupFilteredDeceasedByStatus() {
    // Group by the 'status' field (which should match a statusOption id)
    const deceasedGrouped = _.groupBy(this.filteredDeceased, 'status');
    const prop = this.sortBy === 'firstName' ? 'name' : this.sortBy;
    const secondaryProp = prop === 'surname' ? 'name' : 'surname';
    const dir = this.sortDirection;
    const norm = (v: any) => (v == null ? '' : String(v)).trim().toLowerCase();

    // Sort each group by current sort settings (case-insensitive, trimmed)
    _.forOwn(deceasedGrouped, (patients, status) => {
      deceasedGrouped[status] = _.orderBy(
        patients,
        [(p: any) => norm(p[prop]), (p: any) => norm(p[secondaryProp])],
        [dir, dir]
      );
    });

    this.filteredDeceasedByStatus = deceasedGrouped;
    this.updateDeceasedStatusList();
  }

  updateDeceasedStatusList() {
    if (!this.statusOptions) {
      return;
    }

    // Create sections only for statuses that have matching patients
    const desiredOrder = ['opd', 'inpatient', 'invite'];
    this.patientStatusOptions = _.orderBy(
      _.filter(this.statusOptions, statusOption =>
        _.has(this.filteredDeceasedByStatus, statusOption.id)
      ),
      statusOption => desiredOrder.indexOf(statusOption.id)
    ).map(statusOption => ({
      id: statusOption.id,
      name: statusOption.name,
      patients: this.filteredDeceasedByStatus[statusOption.id] || []
    }));

    // Expand the first section by default (if none is already expanded)
    if (this.patientStatusOptions.length > 0 && this.expandedSection === null) {
      this.expandedSection = this.patientStatusOptions[0].id;
    }
  }

  navigateToPatientFile(event: MouseEvent, patientId: string) {
    event.preventDefault();
    event.stopPropagation();
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }

  toggleExpanded(statusId: string) {
    this.shown = RENDER_BATCH;
    this.expandedSection = this.expandedSection === statusId ? null : statusId;
  }
}
